<template>
    <div class="WRadio">
        <div
            class="Wrapper"
            @click="emitChange"
        >
            <i
                :class="iconClass"
                class="Icon"
            />
            <input
                :id="id"
                ref="radio"
                :value="value"
                :checked="isChecked"
                :name="name"
                :disabled="disabled"
                class="Input"
                type="radio"
                v-on="inputListeners"
            >

            <label
                :for="value"
                class="Label"
                :class="{ error, disabled }"
            >
                {{ label }}
            </label>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { merge } from 'lodash'
import { generateIdMixin } from '@/mixins'

export default {
    name: 'WRadio',
    mixins: [generateIdMixin],
    model: {
        prop: 'modelValue',
        event: 'input',
    },
    props: {
        error: VueTypes.bool.def(false),
        label: VueTypes.string.isRequired,
        modelValue: VueTypes.oneOfType([Boolean, Array, Number, Object, String]).isRequired,
        color: VueTypes.oneOf(['primary', 'secondary', '']).def(''),
        name: VueTypes.string.isRequired,
        disabled: VueTypes.bool.def(false),
        value: VueTypes.oneOfType([Boolean, String, Number, Object]).isRequired,
    },
    computed: {
        isChecked () {
            return this.value === this.modelValue
        },
        iconClass () {
            return [
                this.color,
                {
                    disabled: this.disabled,
                    checked: this.isChecked,
                    error: this.error,
                }]
        },
        inputListeners () {
            return merge({}, this.$listeners, {
                input: (event) => {
                    this.emitChange()
                },
            })
        },
    },
    methods: {
        emitChange () {
            if (!this.disabled) {
                this.$emit('input', this.value)
            }
        },
    },
}
</script>

<style scoped lang="scss">
$icon-size: 16px;

.Wrapper {
	width: fit-content;
	cursor: pointer;

	.Icon {
		position: relative;
		top: 2px;
		display: inline-block;
		width: $icon-size;
		height: $icon-size;
		touch-action: manipulation;

		&:hover {
			cursor: pointer;
		}

		&::before,
		&::after {
			position: absolute;
			top: 0;
			left: 0;
			display: block;
			width: $icon-size;
			height: $icon-size;
			border-radius: 50%;
			box-sizing: border-box;
		}

		&::before {
			content: "";
			border: 1px solid $form-border-color;
			background-color: transparent;
		}

		&::after {
			content: none;
			background-color: $accent-blue;
			transform: scale(0.58);
		}

		&.checked {
			&::after {
				content: "";
			}
		}

		&.error {
			&::before {
				border-color: $danger;
			}
			&::after {
				background-color: $danger;
			}
		}

		&.disabled {
			cursor: default;

			&::before, &::after {
				opacity: .5;
			}
		}

		&.primary {
			&::after{
				background-color: $accent-blue;
			}
		}

		&.secondary {
			&::after{
				background-color: $secondary;
			}
		}
	}

	.Input {
		display: none;
	}

	.Label {
		margin-left: 5px;
		margin-bottom: 0;
		color: $form-color;
		font-weight: $form-font-weight;
		font-size: $form-font-size;

		&:hover {
			cursor: pointer;
		}

		&.error {
			color: $danger;
		}

		&.disabled {
			opacity: .5;
			cursor: default;
		}
	}
}

</style>
